/*
 * @Author: Teemo Ding
 * @Date: 2021-09-15 12:57:17
 * @LastEditTime: 2021-10-12 16:09:57
 * @LastEditors: Teemo Ding
 * @FilePath: /unifyxp-v2-store-portal/src/pages/SalesCannelVersion/SalesChannelSettings/SalesChannelDomain/Edit/SSLCertificate/index.tsx
 */
import { Alert, Button, Label, TextArea } from '@/src/components/Antd';
import SectionWrapper from '@/src/components/Layouts/Section/SectionWrapper';
import { Icon } from '@/src/tools/icon';
import {
  SSLStatusType,
  EnumStatus,
  SSLErrorCodes,
  DistributionStatus,
  SSLMODE,
  ACMEStatus,
} from '@/src/types/common/domain';
import { Collapse, Form } from 'antd';
import moment from 'moment';
import React, { useState } from 'react';
import { useI18n } from 'src/hooks/useRouterChange';
import { sslValid, sslKeyValid } from 'src/tools/regExp';
import {
  i18nGet,
  replaceI18nVariables,
  handleOnGetCurrentSCDateFormat,
} from 'src/tools/tools';
import css from '../css.module.less';

const { Item } = Form;
const { Panel } = Collapse;
interface SSLCertificateProps {
  domain: string;
  domainStatus: string;
  /** 当前使用的证书 */
  certificate: { [key: string]: any } | null;
  /** 更新证书的状态 */
  uploadCertificateStatus: string | null;
  /** 提交 SSL 时禁用按钮 */
  btnDisableWhileReq: boolean;
  /** 是否首次上传，用于展示distribution processing 弹窗 */
  resubmitted: boolean;
  distributionStatus: string | null;
  errorCode: number | null;
  errorMsg: string;
  success: boolean;
  sslMode: SSLMODE;
  acmeStatus: ACMEStatus;
  confirmDomain: (type: any) => Promise<any>;
}
const SSLCertificate: React.FC<SSLCertificateProps> = ({
  domain,
  domainStatus,
  certificate,
  uploadCertificateStatus,
  btnDisableWhileReq,
  resubmitted,
  distributionStatus,
  errorCode,
  errorMsg,
  success,
  sslMode,
  acmeStatus,
  confirmDomain,
}) => {
  const i18n = useI18n();
  const getI18n = (str) => i18nGet(i18n, str);
  const [fold, setFold] = useState(false);
  const [isAlertVisible, setIsAlertVisible] = useState(true);
  const {
    verifiedDate = '',
    expireDate = '',
    status = '',
    remindExpire = false,
  } = certificate || {};
  const disabledAll = status == SSLStatusType.Pending || !domain;
  const deletingStatus = domainStatus === EnumStatus.Deleting;
  const dataformat = handleOnGetCurrentSCDateFormat() || 'MMM DD, YYYY';
  const isACMEMode = sslMode === SSLMODE.ACME;
  const isACMECompleted =
    isACMEMode && acmeStatus === ACMEStatus.VALID && verifiedDate;
  const hasCertificate = certificate && certificate.status;
  const isDistributionCreating =
    distributionStatus === DistributionStatus.Creating;
  const isBYOInputDisable =
    disabledAll ||
    btnDisableWhileReq ||
    (resubmitted && isDistributionCreating);

  const renderAlert = ({
    statusItem,
    success,
    errorCode = 0,
    errorMsg,
    remindExpire = false,
    resubmitted = false,
    btnDisableWhileReq = false,
    showRequestAlert = false,
  }) => {
    // 如果正在请求中 - 需要显示请求弹窗,优先级最高
    if (showRequestAlert && btnDisableWhileReq) {
      return (
        <Alert
          className={css.certificateAlert}
          message={getI18n(
            'salesChannelDomainAndStatus.edit.certificateProcessing',
          )}
          type='info'
          showIcon
          icon={<Icon type='iconpending' />}
          description={getI18n(
            'salesChannelDomainAndStatus.edit.certificateProcessingDes',
          )}
        />
      );
    }

    // 如果正在请求中 - 就不需要显示下面的弹窗了，返回 null（避免重复）
    if (btnDisableWhileReq) return null;

    // 注意：更新证书流程在Submit SSL的时候，后端有异步更新distribution的过程，故此处需要展示该弹窗
    const updateDistribution = resubmitted && isDistributionCreating;

    const shouldShowAlert =
      statusItem === SSLStatusType.Pending ||
      (statusItem === SSLStatusType.Invalid && isAlertVisible) ||
      statusItem === SSLStatusType.Expired ||
      remindExpire ||
      updateDistribution;

    if (!shouldShowAlert) return null;

    let message = '';
    let type: 'info' | 'error' = 'error';
    let iconType = 'iconfailed';
    let description: React.ReactNode = null;

    if (statusItem === SSLStatusType.Expired) {
      //已过期
      type = 'error';
      iconType = 'iconfailed';
      message = getI18n(
        'salesChannelDomainAndStatus.edit.sSLCertificateexpired',
      );
      description =
        sslMode === SSLMODE.BYO
          ? getI18n('salesChannelDomainAndStatus.edit.sSLCertificateexpiredBYO')
          : getI18n(
              'salesChannelDomainAndStatus.edit.sSLCertificateexpiredACME',
            );
    } else if (remindExpire) {
      // 即将过期
      type = 'error';
      iconType = 'iconfailed';
      message = getI18n(
        'salesChannelDomainAndStatus.edit.sSLCertificateexpiressoon',
      );
      description = getI18n(
        'salesChannelDomainAndStatus.edit.sSLCertificateexpiressoondesc',
      );
    } else if (statusItem === SSLStatusType.Pending) {
      // 证书生成中 - 是同步的大概率不会走到这里，但还是保留逻辑
      type = 'info';
      iconType = 'iconpending';
      message = getI18n(
        'salesChannelDomainAndStatus.edit.certificateProcessing',
      );
      description = getI18n(
        'salesChannelDomainAndStatus.edit.certificateProcessingDes',
      );
    } else if (updateDistribution) {
      // 非首次上传证书
      type = 'info';
      iconType = 'iconpending';
      message = getI18n(
        'salesChannelDomainAndStatus.edit.certificateProcessing',
      );
      description = getI18n(
        'salesChannelDomainAndStatus.edit.statusItemPendingBYO',
      );
    } else if (
      statusItem === SSLStatusType.Invalid ||
      SSLErrorCodes.includes(errorCode)
    ) {
      // 无效证书
      type = 'error';
      iconType = 'iconfailed';
      message = getI18n(
        'salesChannelDomainAndStatus.edit.sSLCertificateInvalid',
      );
      description = (
        <>
          {getI18n(
            'salesChannelDomainAndStatus.edit.sSLCertificateInvalidDesc',
          )}
          <br />
          {!success && SSLErrorCodes.includes(errorCode) && (
            <>
              {getI18n(`salesChannelDomainAndStatus.error.${errorCode}`)}
              {errorMsg ? `: ${errorMsg}` : ''}
            </>
          )}
        </>
      );
    }

    return (
      <Alert
        className={css.certificateAlert}
        closable={statusItem === SSLStatusType.Invalid}
        onClose={() => setIsAlertVisible(false)}
        message={message}
        type={type}
        showIcon
        icon={<Icon type={iconType} />}
        description={description}
      />
    );
  };

  const renderItem = (
    <div>
      {sslMode === SSLMODE.BYO &&
        renderAlert({
          statusItem: uploadCertificateStatus,
          success,
          errorCode,
          errorMsg,
          resubmitted,
          btnDisableWhileReq,
          showRequestAlert: hasCertificate,
        })}
      {sslMode === SSLMODE.BYO ? (
        <>
          <div className={css.certificate}>
            <Item
              name={['certificate', 'certificate']}
              className={css.certificateItem}
              label={
                <Label
                  title={`${getI18n(
                    'salesChannelDomainAndStatus.edit.certificate',
                  )}*`}
                />
              }
              rules={[
                {
                  required: true,
                  message: replaceI18nVariables(
                    getI18n('general.fieldisrequired'),
                    getI18n('salesChannelDomainAndStatus.edit.certificate'),
                  ),
                },
                {
                  pattern: sslValid,
                  message: getI18n(
                    'salesChannelDomainAndStatus.edit.validcertificate',
                  ),
                },
              ]}
              labelCol={{ span: 24 }}>
              <TextArea disabled={isBYOInputDisable} size='large' />
            </Item>
            <Item
              name={['certificate', 'primaryKey']}
              className={css.certificateItem}
              rules={[
                {
                  required: true,
                  message: replaceI18nVariables(
                    getI18n('general.fieldisrequired'),
                    getI18n('salesChannelDomainAndStatus.edit.privateKey'),
                  ),
                },
                {
                  pattern: sslKeyValid,
                  message: getI18n(
                    'salesChannelDomainAndStatus.edit.validprivatekey',
                  ),
                },
              ]}
              label={
                <Label
                  title={`${getI18n(
                    'salesChannelDomainAndStatus.edit.privateKey',
                  )}*`}
                />
              }
              labelCol={{ span: 24 }}>
              <TextArea disabled={isBYOInputDisable} size='large' />
            </Item>
          </div>

          <Item
            name={['certificate', 'certificateAuthorityBundle']}
            className={css.certificateItem}
            label={
              <Label
                title={getI18n(
                  'salesChannelDomainAndStatus.edit.certificateAuthorityBundle',
                )}
              />
            }
            rules={[
              {
                pattern: sslValid,
                message: getI18n(
                  'salesChannelDomainAndStatus.edit.validbundle',
                ),
              },
            ]}
            labelCol={{ span: 24 }}>
            <TextArea disabled={isBYOInputDisable} size='large' />
          </Item>
          <Button
            type='primary'
            onClick={async () => {
              await confirmDomain([
                ['certificate', 'certificate'],
                ['certificate', 'primaryKey'],
                ['certificate', 'certificateAuthorityBundle'],
                ['certificate', 'notify'],
                ['domain'],
              ]).then(() => {
                setIsAlertVisible(true);
              });
            }}
            disabled={isBYOInputDisable || deletingStatus}
            className={css.primaryButton}>
            {getI18n('salesChannelDomainAndStatus.edit.submit')}
          </Button>
        </>
      ) : (
        <>
          {' '}
          {isACMECompleted
            ? getI18n('salesChannelDomainAndStatus.edit.sSLCertificateACME')
            : getI18n(
                'salesChannelDomainAndStatus.edit.sSLCertificateACMENoVerify',
              )}
        </>
      )}
    </div>
  );

  return (
    <SectionWrapper
      titleClassName={css.titleContainer}
      containerClassName={css.containerClassName}
      titleOnClick={() => setFold(!fold)}
      rightPart={<Icon type={fold ? 'icondown1' : 'iconright'}></Icon>}
      hideContent={!fold}
      title={getI18n(
        'salesChannelDomainAndStatus.edit.secureSocketsLayerSSLCertificate',
      )}>
      {renderAlert({
        statusItem: status,
        success,
        errorCode,
        errorMsg,
        remindExpire,
        btnDisableWhileReq,
        showRequestAlert: !hasCertificate,
      })}
      {/* 添加Expired为需求UXPSA-14196 */}
      {(status == SSLStatusType.Verified ||
        status === SSLStatusType.Expired) && (
        <div
          className={css.certificateVerified}
          style={{ borderBottom: isACMEMode ? '0px' : '1px solid #f3f3f3;' }}>
          <p className={css.certificateTitle}>
            {getI18n('salesChannelDomainAndStatus.edit.sSLCertificateVerified')}
          </p>
          <p className={css.verifiedData}>
            {verifiedDate &&
              `${getI18n(
                'salesChannelDomainAndStatus.edit.verifiedon',
              )} ${moment(verifiedDate).format(dataformat)}`}
            {expireDate &&
              `, ${getI18n('salesChannelDomainAndStatus.edit.expireson')}
            ${moment(expireDate).format(dataformat)}`}
          </p>
        </div>
      )}

      {status && sslMode === SSLMODE.BYO ? (
        <Collapse className={css.collapse} ghost>
          <Panel
            key='1'
            header={getI18n(
              'salesChannelDomainAndStatus.edit.updateSSLCertificate',
            )}>
            {renderItem}
          </Panel>
        </Collapse>
      ) : (
        renderItem
      )}
    </SectionWrapper>
  );
};

export default SSLCertificate;
